/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

exports.__assign = function() {
    exports.__assign = Object.assign || function __assign(t) {
        var arguments$1 = arguments;

        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments$1[i];
            for (var p in s) { if (Object.prototype.hasOwnProperty.call(s, p)) { t[p] = s[p]; } }
        }
        return t;
    };
    return exports.__assign.apply(this, arguments);
};

// ---------------------------------------------------------------------------
/**
 * Trims the string and then only removes spaces and/or a dash (or en-dash)
 * before the last four of the ten digits.
 *
 * Defaults to returning the (trimmed) original string, if the pattern
 * doesn't match.
 *
 * Cleaned:
 *  * `' 123456-7890'` ==> `'1234567890'`
 *  * `'123456 7890 '` ==> `'1234567890'`
 *  * `' 123456 - 7890'` ==> `'1234567890'`
 *  * `'123456 -7890'` ==> `'1234567890'`
 *
 * Only trimmed:
 * 	* `' abc '` ==> `'abc'`
 *  * `' 123456   - 7890'` ==> `'123456   - 7890'`
 * 	* `'kt. 123456-7890'` ==> `'kt. 123456-7890'`
 * 	* `' 1234-567890'` ==> `'1234-567890'`
 * 	* `'123 456-7890'` ==> `'123 456-7890'`
 */
var cleanKennitalaCareful = function (value) {
    return value.trim().replace(/^(\d{6})\s?[-–]?\s?(\d{4})$/, '$1$2');
};
// ---------------------------------------------------------------------------
/**
 * Aggressively strips away all spaces and dashes (or en-dashes) from the string,
 * as well as any trailing and leading non-digit gunk.
 *
 * Returns whatever is left.
 *
 * Examples:
 *  * `' abc '` ==> `''`
 *  * `'(kt. 123456-7890)'` ==> `'1234567890'`
 *  * `'(kt. 123456-7890, s. 765 4321) '` ==> `'1234567890,s.7654321'`
 *  * `'(tel. 123-4567, 765-4321)'` ==> `'1234567,7654321'`
 *  * `'(s. 765 4321) '` ==> `'7654321'`
 *  * `' 12 34 56 - 78 90'` ==> `'1234567890'`
 *  * `'1-2-3 4-5 6-7-8 9-0'` ==> `'1234567890'`
 */
var cleanKennitalaAggressive = function (value) {
    return value
        .replace(/^\D+/, '')
        .replace(/\D+$/, '')
        .replace(/[\s-–]/g, '');
};
// ---------------------------------------------------------------------------
var cleanIfKtShaped = function (value) {
    var cleaned = cleanKennitalaCareful(value);
    return cleaned.length === 10 && !/\D/.test(cleaned) ? cleaned : undefined;
};
/**
 * Runs minimal cleanup on the input string and if it looks like a kennitala
 * then then inserts a nice separator (default `'-'`) before the last four
 * digits.
 *
 * Defaults to returning the input untouched.
 */
var formatKennitala = function (value, separator) {
    if (separator === void 0) { separator = '-'; }
    var cleaned = cleanIfKtShaped(value);
    if (!cleaned) {
        return value;
    }
    return cleaned.substring(0, 6) + separator + cleaned.substring(6);
};
// ---------------------------------------------------------------------------
/**
 * Returns the (UTC) birth-date (or founding-date) of a "kennitala-shaped" string
 * ...without checking if it is a valid kennitala.
 *
 * It returns `undefined` for malformed (non-kennitala shaped) strings,
 * temporary "kerfiskennitalas" and kennitalas with nonsensical dates, even if
 * they're numerically valid.
 */
var getKennitalaBirthDate = function (value) {
    var cleaned = cleanIfKtShaped(value);
    if (!cleaned || /^[89]/.test(cleaned)) {
        return;
    }
    var DD = String(parseInt(cleaned.substring(0, 2)) % 40).padStart(2, '0');
    var MM = cleaned.substring(2, 4);
    var CC = ((parseInt(cleaned.substring(9, 10)) + 2) % 10) + 18;
    var YY = cleaned.substring(4, 6);
    var ISODate = "".concat(CC + YY, "-").concat(MM, "-").concat(DD);
    var birthDate = new Date(ISODate);
    if (isNaN(birthDate.getTime()) || !birthDate.toISOString().startsWith(ISODate)) {
        return;
    }
    return birthDate;
};
var magic = [3, 2, 7, 6, 5, 4, 3, 2, 1];
var robotKtNums = [
    212, 220, 239, 247, 255, 263, 271, 298, 301, 336, 433, 492, 506, 778,
];
var _robotKtRe;
var isRobotKt = function (value) {
    if (!_robotKtRe) {
        _robotKtRe = new RegExp('010130(' + robotKtNums.join('|') + ')9');
    }
    return _robotKtRe.test(value);
};
var validTypes = { person: 1, company: 1 };
var toKtData = function (data) {
    var value = data.value, type = data.type, robot = data.robot, temporary = data.temporary;
    var ktData = {
        value: value,
        type: type,
        robot: robot,
        toString: function () { return value; },
        get formatted() {
            return formatKennitala(value);
        },
    };
    temporary != null && (ktData.temporary = temporary);
    return ktData;
};
// eslint-disable-next-line complexity
function parseKennitala(value, opts) {
    opts = opts || {};
    if (!value) {
        return;
    }
    value =
        opts.clean === 'none' || opts.clean === false
            ? value
            : opts.clean === 'aggressive'
                ? cleanKennitalaAggressive(value)
                : cleanKennitalaCareful(value);
    if (value.length !== 10 || /\D/.test(value)) {
        return;
    }
    if (/^[89]/.test(value) && !opts.rejectTemporary && opts.type !== 'company') {
        /*
          Skráning í kerfiskennitöluskrá er eingöngu fyrir einstaklinga sem
          dvelja skemur en 3-6 mánuði á Íslandi eða munu ekki dvelja hér á landi.
          [Þær] samanstanda af tíu tölustöfum og byrja ávallt á 8 eða 9
          og hinar tölurnar verða tilviljanakenndar.
          https://www.skra.is/folk/eg-i-thjodskra/um-kennitolur/um-kerfiskennitolur/
        */
        return toKtData({
            value: value,
            type: 'person',
            robot: false,
            temporary: true,
        });
    }
    // Quickly weed out obviously non-date kennitalas
    // (Example of one such checkSum-valid but nonsensical kennitala: "3368492689")
    // Here we trade a few false positives for speed:
    // A value starting with "310290..." (Feb. 31st) might pass
    if (!/^(?:[012456]\d|[37][01])(?:0\d|1[012]).+[890]$/.test(value)) {
        return;
    }
    // optionally perform slower, more rigorous date parsing
    if (opts.strictDate && !getKennitalaBirthDate(value)) {
        return;
    }
    var robot = isRobotKt(value);
    if (robot && !opts.robot) {
        return;
    }
    var checkSum = magic.reduce(function (acc, num, i) { return acc + num * parseInt(value[i]); }, 0);
    if (checkSum % 11) {
        return;
    }
    var type = value[0] > '3' ? 'company' : 'person';
    var optType = opts.type;
    if (optType && optType in validTypes && optType !== type) {
        return;
    }
    return toKtData({ value: value, type: type, robot: robot });
}
function isValidKennitala(value, opts) {
    return !!parseKennitala(value, exports.__assign(exports.__assign({}, opts), { 
        // make "clean: false" the default when validating
        clean: (opts === null || opts === void 0 ? void 0 : opts.clean) || false }));
}
// ---------------------------------------------------------------------------
/**
 * Detects if an input `Kennitala` is `KennitalaPerson`.
 *
 * Assumes that the input `kt` is already validated as `Kennitala`
 * and performs no internal validation, and is thus insanely fast, but
 * unreliable for random strings.
 *
 * To safely check the type of a plain, non-validated `string` input,
 * use `parseKennitala` and check the `.type` of the retured data object.
 *
 * Example:
 *
 * ```js
 * const isPerson = parseKennitala(someString)?.type === 'person';
 * ```
 * ...or...
 * ```js
 * const isPerson = !!parseKennitala(someString, { type: 'person' });
 * ```
 */
var isPersonKennitala = function (kennitala) {
    // Temporary "kerfiskenntalas" for people start with 8 or 9
    return /^[012389]/.test(kennitala);
};
/**
 * Detects if an input `Kennitala` is `KennitalaCompany`.
 *
 * Assumes that the input `kt` is already validated as `Kennitala`
 * and performs no internal validation, and is thus insanely fast, but
 * unreliable for random strings.
 *
 * To safely check the type of a plain, non-validated `string` input,
 * use `parseKennitala` and check the `.type` of the retured data object.
 *
 * Example:
 *
 * ```js
 * const isCompany = parseKennitala(someString)?.type === 'company';
 * ```
 * ...or...
 * ```js
 * const isCompany = !!parseKennitala(someString, { type: 'company' });
 * ```
 */
var isCompanyKennitala = function (kennitala) {
    return /^[4567]/.test(kennitala);
};
/**
 * Detects if an input `Kennitala` is a (temporary) "kerfiskennitala"
 * (a subset of valid `KennitalaPerson`s).
 *
 * Assumes that the input `kt` is already validated as `Kennitala`
 * and performs no internal validation, and is thus insanely fast, but
 * unreliable for random strings.
 *
 * To safely check the type of a plain, non-validated `string` input,
 * use `parseKennitala` and check the `.temporary` status of the
 * retured data object.
 *
 * Example:
 *
 * ```js
 * const isTemp = !!parseKennitala(someString)?.temporary;
 * ```
 */
var isTempKennitala = function (kennitala) {
    return /^[89]/.test(kennitala);
};
function generateKennitala(opts) {
    if (opts === void 0) { opts = {}; }
    var random = Math.random, floor = Math.floor;
    var isCompany = opts.type === 'company';
    if (!isCompany) {
        if (opts.temporary) {
            var Head = String(8 + (random() > 0.5 ? 1 : 0));
            var Tail = String(floor(random() * 1000000000)).padStart(9, '0');
            return (Head + Tail);
        }
        if (opts.robot) {
            var RRR = robotKtNums[floor(random() * robotKtNums.length)];
            return "010130".concat(RRR, "9");
        }
    }
    var bDay = opts.birthDate;
    if (!bDay ||
        isNaN(bDay.getTime()) ||
        // Treat dates outside of the 1800-2099 range as invalid
        bDay < new Date('1800-01-01') ||
        bDay >= new Date('2100-01-01')) {
        var YEAR_MS = 365 * 24 * 60 * 60 * 1000;
        bDay = new Date(Date.now() - floor(random() * 100 * YEAR_MS));
    }
    var dateModifier = isCompany ? 40 : 0;
    // Build the predictable segments of the kennitala
    var DDMMYY = String(bDay.getUTCDate() + dateModifier).padStart(2, '0') +
        String(bDay.getUTCMonth() + 1).padStart(2, '0') +
        String(bDay.getUTCFullYear() % 100).padStart(2, '0');
    var C = String(bDay.getUTCFullYear())[1];
    var kt = '';
    // Brute-force search for a checksum digit that passes validation.
    // NOTE: This is slow, but `generateKennitala` is generally not used
    // in performance-critical code-paths.
    // Open a GitHub issue if you need a faster implementation.
    while (true) {
        var x = 0; // Checksum digit
        var RR = isCompany
            ? String(floor(100 * random()))
            : String(floor(20 + 80 * random()));
        while (x < 10) {
            kt = DDMMYY + RR + x + C;
            if (isValidKennitala(kt, { type: opts.type })) {
                return kt;
            }
            x++;
        }
    }
    return kt;
}

exports.cleanKennitalaAggressive = cleanKennitalaAggressive;
exports.cleanKennitalaCareful = cleanKennitalaCareful;
exports.formatKennitala = formatKennitala;
exports.generateKennitala = generateKennitala;
exports.getKennitalaBirthDate = getKennitalaBirthDate;
exports.isCompanyKennitala = isCompanyKennitala;
exports.isPersonKennitala = isPersonKennitala;
exports.isTempKennitala = isTempKennitala;
exports.isValidKennitala = isValidKennitala;
exports.parseKennitala = parseKennitala;
